import React, { useEffect, useState } from 'react';
import DateInput from '../components/DateInput';
import api from '../api/axios';
import { format } from 'date-fns';
import { useSearchParams } from 'react-router-dom';

const fmtDate = d => d ? format(new Date(d), 'dd/MM/yyyy') : '—';

export default function Reports() {
  const [searchParams] = useSearchParams();
  const [tab, setTab] = useState(searchParams.get('tab') || 'shipment-status');
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      const r = await api.get(`/reports/${tab}`, { params });
      setData(r.data);
    } catch { setData([]); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [tab]);

  const exportExcel = () => {
    const token = localStorage.getItem('token');
    window.open(`/api/reports/export/${tab}?token=${token}`, '_blank');
  };

  const TABS = [
    ['shipment-status','Shipment Status'],
    ['payment-due','Payment Due'],
    ['material-received','Material Received'],
    ['complaints','Complaint Report'],
  ];

  const renderTable = () => {
    if (loading) return <div className="loading">Loading...</div>;
    if (data.length === 0) return <div className="empty-state">No data for selected period</div>;

    if (tab === 'shipment-status') return (
      <table><thead><tr><th>Order No</th><th>Shipper</th><th>Quality</th><th>Origin</th><th>BL No</th><th>BL Date</th><th>Bill Wt</th><th>Port Status</th><th>Payment</th><th>Due Date</th></tr></thead>
      <tbody>{data.map((r,i)=><tr key={i}><td className="fw-bold">{r.order_no}</td><td>{r.shipper_name}</td><td>{r.quality}</td><td>{r.origin_country}</td><td>{r.bl_no}</td><td>{fmtDate(r.date_of_bl)}</td><td>{r.bill_weight_mt} MT</td><td><span className="badge badge-blue">{r.port_status}</span></td><td><span className={`badge ${r.payment_status==='Paid'?'badge-green':r.payment_status==='Partial'?'badge-yellow':'badge-red'}`}>{r.payment_status}</span></td><td style={{color:r.due_date&&new Date(r.due_date)<new Date()&&r.payment_status!=='Paid'?'var(--accent)':'inherit'}}>{fmtDate(r.due_date)}</td></tr>)}</tbody></table>
    );

    if (tab === 'payment-due') return (
      <table><thead><tr><th>Order No</th><th>Shipper</th><th>BL No</th><th>Due Date</th><th>Amount Due (USD)</th><th>Status</th><th>Alert</th></tr></thead>
      <tbody>{data.map((r,i)=><tr key={i}><td className="fw-bold">{r.order_no}</td><td>{r.shipper_name}</td><td>{r.bl_no}</td><td style={{color:r.alert==='Overdue'?'var(--accent)':'inherit'}}>{fmtDate(r.due_date)}</td><td>${parseFloat(r.actual_amount_due||0).toLocaleString()}</td><td><span className={`badge ${r.payment_status==='Paid'?'badge-green':r.payment_status==='Partial'?'badge-yellow':'badge-red'}`}>{r.payment_status}</span></td><td><span className={`badge ${r.alert==='Overdue'?'badge-red':r.alert==='Due Today'?'badge-yellow':'badge-green'}`}>{r.alert}</span></td></tr>)}</tbody></table>
    );

    if (tab === 'material-received') return (
      <table><thead><tr><th>Order No</th><th>Shipper</th><th>Quality</th><th>Contract Wt</th><th>Received Wt</th><th>Shortfall</th><th>Receipt Date</th><th>Slip No</th><th>Received By</th></tr></thead>
      <tbody>{data.map((r,i)=><tr key={i}><td className="fw-bold">{r.order_no}</td><td>{r.shipper_name}</td><td>{r.quality}</td><td>{r.contract_weight_mt} MT</td><td>{r.total_received_weight_mt} MT</td><td style={{color:parseFloat(r.shortfall_mt)>0?'var(--accent)':'var(--success)'}}>{r.shortfall_mt} MT</td><td>{fmtDate(r.receipt_date)}</td><td>{r.slip_no}</td><td>{r.received_by}</td></tr>)}</tbody></table>
    );

    if (tab === 'complaints') return (
      <table><thead><tr><th>Date</th><th>Order No</th><th>Shipper</th><th>Type</th><th>Description</th><th>Reported To</th><th>Status</th></tr></thead>
      <tbody>{data.map((r,i)=><tr key={i}><td>{fmtDate(r.complaint_date)}</td><td className="fw-bold">{r.order_no}</td><td>{r.shipper_name}</td><td><span className="badge badge-orange">{r.complaint_type}</span></td><td>{r.description}</td><td>{r.reported_to}</td><td><span className={`badge ${r.status==='Resolved'?'badge-green':r.status==='Open'?'badge-red':'badge-yellow'}`}>{r.status}</span></td></tr>)}</tbody></table>
    );
  };

  return (
    <div>
      <div className="page-header">
        <div><h2>Reports</h2><p>Export and view operational reports</p></div>
        <button className="btn btn-success" onClick={exportExcel}>Export to Excel</button>
      </div>

      <div className="tabs">
        {TABS.map(([k,l]) => <div key={k} className={`tab${tab===k?' active':''}`} onClick={()=>setTab(k)}>{l}</div>)}
      </div>

      <div className="filter-bar">
        <div className="form-group"><label>From Date</label><DateInput className="form-control" value={dateFrom} onChange={e=>setDateFrom(e.target.value)} /></div>
        <div className="form-group"><label>To Date</label><DateInput className="form-control" value={dateTo} onChange={e=>setDateTo(e.target.value)} /></div>
        <button className="btn btn-primary btn-sm" onClick={load}>Apply Filter</button>
        <button className="btn btn-secondary btn-sm" onClick={()=>{setDateFrom('');setDateTo('');setTimeout(load,50);}}>Clear</button>
        <span className="text-muted" style={{fontSize:'12px',marginLeft:'auto'}}>{data.length} records</span>
      </div>

      <div className="card" style={{padding:0}}>
        <div className="table-wrap">{renderTable()}</div>
      </div>
    </div>
  );
}
